// File: frontend/src/components/profile/ProfileEntryAccordion.tsx
// src/components/profile/ProfileEntryAccordion.tsx

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EntryModel } from "../../models";
import { EntryRow } from "../entries/EntryRow";
import { generateMatchUrlFromEntry } from "../../utils";
import "./ProfileEntryAccordion.css";

// Which vote metric should be visually highlighted in the header badge area
export type VoteHighlight = "plus" | "minus" | "fav" | "none";

interface ProfileEntryAccordionProps {
  entry: EntryModel;
  onVoteUpdate?: (entryId: number, updates: Partial<EntryModel>) => void;
  defaultExpanded?: boolean;
  // Issue C: pass-through props so EntryRow's pin button works inside the accordion
  isPinned?: boolean;
  onPinChange?: (pinnedEntry: EntryModel | null) => void;
  // Which vote count to highlight (driven by the active sub-tab)
  highlightVote?: VoteHighlight;
  // Whether to show vote badges at all — only true for Genel Bilgiler tab
  showVoteBadges?: boolean;
  // Called after the entry is successfully soft-deleted so the parent list can
  // drop the row immediately instead of waiting for a manual page refresh
  onDelete?: (entryId: number) => void;
}

export const ProfileEntryAccordion: React.FC<ProfileEntryAccordionProps> = ({
  entry,
  onVoteUpdate,
  defaultExpanded = false,
  isPinned = false,
  onPinChange,
  highlightVote = "none",
  showVoteBadges = false,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Determine entry type: person, club, team, custom topic, or match (in priority order)
  const isPersonEntry = !!entry.personId;
  const isClubEntry  = !isPersonEntry && !!entry.clubId;
  const isTeamEntry  = !isPersonEntry && !isClubEntry && !!entry.teamId;
  const isCustomTopicEntry = !isPersonEntry && !isClubEntry && !isTeamEntry && !!entry.customTopicId;

  // Person entry: /kisi/{personSlug}, title = full name.
  // Must use the slug — the /api/people/by-slug endpoint only resolves slugs,
  // so linking by personId produced a 404 (PERSON_NOT_FOUND) for people whose
  // slug differs from their id.
  const personUrl   = `/kisi/${entry.personSlug}`;
  const personTitle = entry.personFullName ?? "—";

  // Club entry: /kulup/{clubSlug}, title = club name
  const clubUrl   = `/kulup/${entry.clubSlug}`;
  const clubTitle = entry.clubName ?? "—";

  // Team entry: /takim/{clubSlug}/{teamSlug}, title = team name
  // teamSlug on the entry is the team-specific slug; clubSlug is shared with the club
  const teamUrl   = `/takim/${entry.clubSlug}/${entry.teamSlug}`;
  const teamTitle = entry.teamName ?? "—";

  // Custom topic ("Gündem") entry: /gundem/{customTopicSlug}, title = topic title
  const customTopicUrl   = `/gundem/${entry.customTopicSlug}`;
  const customTopicTitle = entry.customTopicTitle ?? "—";

  // Match entry: URL generated from match data, title = "Home - Away"
  const matchUrl   = generateMatchUrlFromEntry(
    entry.homeTeam,
    entry.awayTeam,
    entry.matchDate,
    entry.matchId
  );
  const matchTitle = `${entry.homeTeam} - ${entry.awayTeam}`;

  // Resolve the correct URL and display title based on entry type
  let entryUrl: string;
  let entryTitle: string;

  if (isPersonEntry) {
    entryUrl   = personUrl;
    entryTitle = personTitle;
  } else if (isClubEntry) {
    entryUrl   = clubUrl;
    entryTitle = clubTitle;
  } else if (isTeamEntry) {
    entryUrl   = teamUrl;
    entryTitle = teamTitle;
  } else if (isCustomTopicEntry) {
    entryUrl   = customTopicUrl;
    entryTitle = customTopicTitle;
  } else {
    // Default: match entry
    entryUrl   = matchUrl;
    entryTitle = matchTitle;
  }

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`profile-entry-accordion ${isExpanded ? "expanded" : ""}`}>
      {/* Accordion Header — entry title + vote badges + toggle */}
      <div className="accordion-header" onClick={toggleAccordion}>

        {/* Left: title link — person name, club name, team name, or match title */}
        <div className="accordion-title-wrapper">
          <Link
            to={entryUrl}
            className="accordion-match-link"
            onClick={(e) => e.stopPropagation()}
          >
            {entryTitle}
          </Link>
        </div>

        {/* Center: vote score badges — only shown in Genel Bilgiler tab */}
        {showVoteBadges && (
          <div className="accordion-vote-badges">
            <span
              className={`vote-badge vote-badge--plus ${
                highlightVote === "plus" ? "vote-badge--highlighted" : ""
              }`}
            >
              <span className="vote-badge__icon">+</span>
              <span className="vote-badge__count">{entry.plusCount}</span>
            </span>

            <span
              className={`vote-badge vote-badge--minus ${
                highlightVote === "minus" ? "vote-badge--highlighted" : ""
              }`}
            >
              <span className="vote-badge__icon">−</span>
              <span className="vote-badge__count">{entry.minusCount}</span>
            </span>

            <span
              className={`vote-badge vote-badge--fav ${
                highlightVote === "fav" ? "vote-badge--highlighted" : ""
              }`}
            >
              <span className="vote-badge__icon">&#x2605;&#xFE0E;</span>
              <span className="vote-badge__count">{entry.favCount}</span>
            </span>
          </div>
        )}

        {/* Right: expand/collapse toggle */}
        <div className="accordion-toggle">
          <span className="accordion-icon">{isExpanded ? "▼" : "▶"}</span>
        </div>
      </div>

      {/* Accordion Content — Entry Row */}
      {isExpanded && (
        <div className="accordion-content">
          <EntryRow
            entry={entry}
            onVoteUpdate={onVoteUpdate}
            isPinned={isPinned}
            onPinChange={onPinChange}
            onDelete={onDelete}
          />
        </div>
      )}
    </div>
  );
};